import { useStore } from 'vuex'
import { defineNuxtPlugin, useRouter } from '#app'

declare global {
  interface Window {
    pendo: any
  }
}

function mapToArray(inputMap: Record<string, string[]>): string[] {
  // Initialize an empty array to store the result
  const resultArray: string[] = []

  try {
    // Iterate through each key in the input map
    for (const key in inputMap) {
      // Get the array of values for the current key
      const values = inputMap[key]

      // Iterate through each value and construct the key-value pairs
      for (let i = 0; i < values.length; i++) {
        // Construct the key-value pair and push it to the result array
        resultArray.push(`${key}:${values[i]}`)
      }
    }
  }
  catch (e) {
    console.error('Failed to build permissions array')
  }

  // Return the final result array
  return resultArray
}

export default defineNuxtPlugin(() => {
  const router = useRouter()
  const store = useStore()
  const config = useRuntimeConfig()

  const initializePendo = (account: any, permissions: string[], promoter: any, userRoles: any) => {
    // Get the current role name from the role OID
    let currentRoleName: string | null = null
    if (account?.defaultUserRoleOid && userRoles?.defaultUserRoles) {
      const role = userRoles.defaultUserRoles.find((r: any) => r.oid === account.defaultUserRoleOid)
      currentRoleName = role?.name || null
    }
    else if (account?.promoterUserRoleOid && userRoles?.promoterUserRoles) {
      const role = userRoles.promoterUserRoles.find((r: any) => r.oid === account.promoterUserRoleOid)
      currentRoleName = role?.name || null
    }

    const currentAm3Version = config.public.GIT_HASH || 'local'

    // Please use Strings, Numbers, or Bools for value types.
    const pendoParams: {
      visitor: Record<string, any>
      account: Record<string, any>
    } = {
      visitor: {
        id: account.oid, // Required if user is logged in
        email: account.emailAddress,
        firstName: account.firstName,
        lastName: account.lastName,
        full_name: `${account.firstName} ${account.lastName}`,
        role: currentRoleName,
        permissions,
        companyCategory: promoter?.companyCategory || null,
        creationDate: promoter?.sysCtime || null,
        country: account.companyCountry || null,
        platform: 'am3',
        currentAm3Version,
      },

      account: {
        id: account.promoterOid,
        companyName: account.companyName || promoter?.name || null,
        companyCategory: promoter?.companyCategory || null,
        companyCreationDate: promoter?.sysCtime || null,
        platform: 'am3',
      },
    }

    /*
     * A.R. 2022-06-21: Mapping Feature Object into boolean fields
     * ----------------------------
     * 'feature' is a field in auth.account that contains an object of features that an account may have enabled
     * Example structure is
     *   feature: { featureKey: { id: 'feature-key', oid: 111, enabled: true, components: { componentKey: { id: 'component-key', oid: 122, enabled: false } } } }
     * Features may have 'components' inside them with the same structure of 'id', 'oid', and 'enabled'.
     * Since Pendo does not accept Objects, we have to map them out to single booleans.
     */
    if (account.features) {
      const featureKeys = Object.keys(account.features)

      if (featureKeys && featureKeys.length > 0) {
        featureKeys.forEach((feature) => {
          const featureContent = account.features[feature]
          const feature_label = `is_feature_${featureContent.id.replaceAll('-', '_')}_enabled`
          pendoParams.account[feature_label] = featureContent.enabled

          if (featureContent.components) {
            const featureComponentKeys = Object.keys(featureContent.components)

            if (featureComponentKeys && featureComponentKeys.length > 0) {
              featureComponentKeys.forEach((featureComponent) => {
                const featureComponentContent = account.features[feature].components[featureComponent]
                // Pendo allow use only letters, digits and underscores
                const feature_component_label = `is_feature_${feature}_${featureComponentContent.id.replaceAll('-', '_')}_enabled`
                pendoParams.account[feature_component_label] = featureComponentContent.enabled
              })
            }
          }
        })
      }
    }

    if (typeof window.pendo === 'undefined' || !window.pendo.validateInstall) {
      // Pendo injection script
      (function (p: any, e: Document, n: string, d: string) {
        let v: string[]
        let w: number
        let x: number
        let y: HTMLScriptElement
        let z: HTMLElement | null
        let o: any = p[d] = p[d] || {}
        o._q = o._q || []
        v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track']
        for (w = 0, x = v.length; w < x; ++w)(function (m: string) {
          o[m] = o[m] || function (...args: any[]) { o._q[m === v[0] ? 'unshift' : 'push']([m].concat(args)) }
        })(v[w])
        y = e.createElement(n) as HTMLScriptElement
        y.async = true
        y.src = `https://cdn.pendo.io/agent/static/${config.public.arPendoAPIKey}/pendo.js`
        z = e.getElementsByTagName(n)[0]
        if (z?.parentNode) {
          z.parentNode.insertBefore(y, z)
        }
      })(window, document, 'script', 'pendo')
      // Pendo has not been initialized yet
      // Call this function after users are authenticated in your app and your visitor and account id values are available
      window.pendo.initialize(pendoParams)
    }
    else {
      // Pendo has been previously initialized and the data has to be updated.
      // This is necessary if the user accesses multiple accounts in the same browser.
      window.pendo.identify(pendoParams)
    }
  }

  /*
   ** Every time the route changes (fired on initialization too)
   */
  router.afterEach(() => {
    const auth = store.state.authModule

    // An account must be logged in for Pendo to be initialized/identified.
    if (!auth || !auth.account || !config.public.arPendoAPIKey) { return }
    const permissionsArray = auth.permissions ? mapToArray(auth.permissions) : []

    initializePendo(auth.account, permissionsArray, auth.promoter, {
      defaultUserRoles: auth.defaultUserRoles,
      promoterUserRoles: auth.promoterUserRoles,
    })
  })
})
